import Personnummer from 'personnummer';
import { AuthMethod } from 'models/user';

/**
 * The login methods the user forms offer. Mirrors resolveAdminAuthMethod in
 * shared-denthub-code, which is what actually decides.
 *
 * Any admin may choose password or SITHS. DD-3552: only Denthub Support moves a user
 * onto or off an e-ID, because BankID may not be used to create or restore another
 * credential; Support does that in the super admin UI (DD-3642). Company SSO and
 * integration users are managed elsewhere too.
 */
const ADMIN_SELECTABLE_AUTH_METHODS: readonly AuthMethod[] = [AuthMethod.PASSWORD, AuthMethod.SITHS];

/** e-IDs matched on the person's number, which the user forms ask for. */
export const PERSONAL_IDENTITY_NUMBER_AUTH_METHODS: readonly AuthMethod[] = [AuthMethod.BANKID_SE, AuthMethod.FTN_FI];

export const isAuthMethodEditable = (current: AuthMethod | undefined): boolean =>
    !current || ADMIN_SELECTABLE_AUTH_METHODS.includes(current);

/** The selector's options: the choices, plus the current method when it is not one. */
export const adminAuthMethodOptions = (current: AuthMethod | undefined): AuthMethod[] =>
    current && !ADMIN_SELECTABLE_AUTH_METHODS.includes(current)
        ? [...ADMIN_SELECTABLE_AUTH_METHODS, current]
        : [...ADMIN_SELECTABLE_AUTH_METHODS];

export const requiresPersonalIdentityNumber = (method: AuthMethod | undefined): boolean =>
    !!method && PERSONAL_IDENTITY_NUMBER_AUTH_METHODS.includes(method);

// Finnish identity code: DDMMYY, century sign, NNN, check character (DDMMYYNNN mod 31).
const HETU_PATTERN = /^(\d{6})[-+YXWVUABCDEF](\d{3})([0-9A-FHJ-NPR-Y])$/;
const HETU_CHECK_CHARACTERS = '0123456789ABCDEFHJKLMNPRSTUVWXY';

const isValidFinnishIdentityCode = (value: string): boolean => {
    const match = HETU_PATTERN.exec(value.trim().toUpperCase());
    return !!match && HETU_CHECK_CHARACTERS[Number(`${match[1]}${match[2]}`) % 31] === match[3];
};

/** A light check for the form; the backend validates in full, dates included. */
export const isValidPersonalIdentityNumber = (method: AuthMethod | undefined, value: string | undefined): boolean => {
    if (!value) {
        return false;
    }
    return method === AuthMethod.FTN_FI ? isValidFinnishIdentityCode(value) : Personnummer.valid(value);
};
